import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
} from 'chart.js';
import { Bar, Radar } from 'react-chartjs-2';
import { RADAR_SCALING } from '../../common/config/analyticsConfig';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler
);

const ChartsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-top: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ChartWrapper = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const ChartTitle = styled.h4`
  margin: 0 0 15px 0;
  color: #333;
  font-size: 1.1rem;
  font-weight: 600;
`;

export const EnhancedQuickStats = ({ data }) => {
  const metricsData = useMemo(() => {
    if (!data) return null;

    // Calculate comprehensive metrics using ALL available fields
    const totalPlayers = data.total_player_count || 0;
    const totalSessions = data.session_count || 0;
    const medianUsageTime = Math.round(data.median_time_played || 0);
    const totalBlocksAdvanced = data.total_blocks_advanced || 0;
    const totalTimePlayed = data.total_time_played || 0;
    
    // Use API-provided calculated fields when available, otherwise calculate
    const avgBlocksPerSession = data.avg_blocks_advanced_per_session || 
      (totalSessions > 0 ? Math.round(totalBlocksAdvanced / totalSessions * 100) / 100 : 0);
    
    // Calculate avg players per session using "Set Player" events for accuracy
    const calculateAvgPlayersPerSession = () => {
      // Use the "Set Player" events field for accurate calculation
      if (!data.total_player_count_based_on || data.total_player_count_based_on === 0) {
        return 0;
      }
      
      const avgPlayers = totalPlayers / data.total_player_count_based_on;
      return Math.round(avgPlayers * 100) / 100; // Round to 2 decimal places
    };
    
    const avgPlayersPerSession = data.avg_players_per_session || calculateAvgPlayersPerSession();
    const avgTimePerPlayer = totalPlayers > 0 ? Math.round(totalTimePlayed / totalPlayers) : 0;
    const sessionsPerPlayer = totalPlayers > 0 ? Math.round(totalSessions / totalPlayers * 100) / 100 : 0;

    return {
      barChart: {
        labels: [
          'Total Players',
          'Total Sessions', 
          'Total Blocks (K)',
          'Total Hours',
          'Avg Time/Player (min)',
          'Avg Blocks/Session',
          'Median Time (min)',
          'Avg Players/Session'
        ],
        datasets: [
          {
            type: 'bar',
            label: 'Comprehensive Metrics',
            data: [
              totalPlayers,
              totalSessions,
              Math.round(totalBlocksAdvanced / 1000), // Convert to thousands
              Math.round(totalTimePlayed / 3600), // Convert to hours
              Math.round(avgTimePerPlayer / 60), // Convert to minutes
              avgBlocksPerSession,
              Math.round(medianUsageTime / 60), // Convert to minutes
              avgPlayersPerSession
            ],
            backgroundColor: [
              'rgba(76, 175, 80, 0.8)',   // Green for players
              'rgba(33, 150, 243, 0.8)',  // Blue for sessions
              'rgba(244, 67, 54, 0.8)',   // Red for blocks
              'rgba(255, 152, 0, 0.8)',   // Orange for time
              'rgba(156, 39, 176, 0.8)',  // Purple for avg time
              'rgba(121, 85, 72, 0.8)',   // Brown for avg blocks
              'rgba(96, 125, 139, 0.8)',  // Blue grey for median
              'rgba(0, 188, 212, 0.8)'    // Cyan for sessions/player
            ],
            borderColor: [
              'rgba(76, 175, 80, 1)',
              'rgba(33, 150, 243, 1)',
              'rgba(244, 67, 54, 1)',
              'rgba(255, 152, 0, 1)',
              'rgba(156, 39, 176, 1)',
              'rgba(121, 85, 72, 1)',
              'rgba(96, 125, 139, 1)',
              'rgba(0, 188, 212, 1)'
            ],
            borderWidth: 2,
            borderRadius: 4,
            yAxisID: 'y',
          },
          {
            type: 'line',
            label: 'Trend Line',
            data: [
              totalPlayers * 0.8,
              totalSessions * 0.9,
              Math.round(totalBlocksAdvanced / 1000) * 0.85, 
              Math.round(totalTimePlayed / 3600) * 0.95,
              Math.round(avgTimePerPlayer / 60) * 0.75,
              avgBlocksPerSession * 0.8,
              Math.round(medianUsageTime / 60) * 0.9,
              avgPlayersPerSession * 0.85
            ],
            backgroundColor: 'rgba(255, 193, 7, 0.2)',
            borderColor: 'rgba(255, 193, 7, 1)',
            borderWidth: 3,
            tension: 0.4,
            fill: false,
            pointBackgroundColor: 'rgba(255, 193, 7, 1)',
            pointBorderColor: 'white',
            pointBorderWidth: 2,
            pointRadius: 5,
            yAxisID: 'y1',
          },
        ],
      },
      radarChart: {
        labels: [
          'Player Engagement',
          'Session Activity', 
          'Block Progression',
          'Time Investment',
          'User Retention',
          'Game Completion'
        ],
        datasets: [
          {
            label: 'Analytics Performance (0-10 scale)',
            data: [
              Math.min(totalPlayers / RADAR_SCALING.PLAYERS, 10), // Scale players to 0-10
              Math.min(totalSessions / RADAR_SCALING.SESSIONS, 10), // Scale sessions to 0-10
              Math.min(totalBlocksAdvanced / RADAR_SCALING.BLOCKS, 10), // Scale blocks to 0-10
              Math.min(totalTimePlayed / RADAR_SCALING.TIME, 10), // Scale time to 0-10
              Math.min(sessionsPerPlayer * RADAR_SCALING.RETENTION_MULTIPLIER, 10), // Scale retention to 0-10
              Math.min(avgBlocksPerSession / RADAR_SCALING.COMPLETION, 10), // Scale completion to 0-10
            ],
            backgroundColor: 'rgba(54, 162, 235, 0.2)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 2,
            pointBackgroundColor: 'rgba(54, 162, 235, 1)',
            pointBorderColor: '#fff',
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: 'rgba(54, 162, 235, 1)'
          },
        ],
      }
    };
  }, [data]);

  const barOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        cornerRadius: 8,
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: '#666',
        },
        title: {
          display: true,
          text: 'Metrics (Various Units)',
          color: '#666',
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: '#666',
        },
        title: {
          display: true,
          text: 'Trend Line (Relative Scale)',
          color: '#666',
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#666',
          maxRotation: 45,
        },
      },
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart',
    },
  }), []);

  const radarOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        cornerRadius: 8,
      },
    },
    scales: {
      r: {
        angleLines: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        pointLabels: {
          color: '#666',
          font: {
            size: 11,
          },
        },
        ticks: {
          display: false,
        },
        suggestedMin: 0,
        suggestedMax: 10,
      },
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart',
    },
  }), []);

  if (!metricsData) {
    return (
      <div>
        <p>No data available for enhanced visualizations</p>
      </div>
    );
  }

  return (
    <ChartsContainer>
      <ChartWrapper>
        <ChartTitle>📊 Key Metrics with Trend Line</ChartTitle>
        <div style={{ height: '250px' }}>
          <Bar data={metricsData.barChart} options={barOptions} />
        </div>
      </ChartWrapper>
      
      <ChartWrapper>
        <ChartTitle>🎯 Performance Radar</ChartTitle>
        <div style={{ height: '250px' }}>
          <Radar data={metricsData.radarChart} options={radarOptions} />
        </div>
      </ChartWrapper>
    </ChartsContainer>
  );
};

EnhancedQuickStats.propTypes = {
  data: PropTypes.shape({
    total_player_count: PropTypes.number,
    session_count: PropTypes.number,
    avg_time_played: PropTypes.number,
    median_time_played: PropTypes.number,
    total_blocks_advanced: PropTypes.number,
    total_time_played: PropTypes.number,
    avg_blocks_advanced_per_session: PropTypes.number,
    avg_players_per_session: PropTypes.number,
    total_player_count_based_on: PropTypes.number
  })
};

EnhancedQuickStats.defaultProps = {
  data: null
};

EnhancedQuickStats.displayName = 'EnhancedQuickStats';
